/**
 * Live Workspace data arrives as socket frames — about a dozen a second with
 * every Account in Scope. Applying each one re-rendered the whole app, so the
 * frames are collected here and applied together on a fixed tick (one render
 * per tick, however busy the Accounts are).
 */
import type { WorkspaceEvent } from './activity';
import { chatKey, type ChatStateRow } from './chats';

/** How often collected frames are applied. */
export const LIVE_TICK_MS = 500;
/** Events kept for Activity (about two hours on a busy Scope once the noise is left out). */
export const MAX_EVENTS = 3000;

export interface LiveSnapshot {
  /** Events Activity can show, oldest first. */
  events: WorkspaceEvent[];
  chatStates: Map<string, ChatStateRow>;
}

export const EMPTY_LIVE: LiveSnapshot = { events: [], chatStates: new Map() };

/** What one tick applies. */
export interface LiveDrain {
  snapshot: LiveSnapshot;
  /** Live (not replayed) events in arrival order, for onEvent listeners — all kinds. */
  live: WorkspaceEvent[];
  /** Chat state rows that changed this tick (latest per chat). */
  states: ChatStateRow[];
  /** The latest badge count, or null when none arrived. */
  badge: number | null;
}

/** Which list events Activity can show (from Activity settings). */
export interface ListAccounts {
  /** Accounts with at least one watched list. */
  watched: ReadonlySet<string>;
  /** Fans the AI ignores are switched on. */
  aiIgnored: boolean;
}

export const NO_LIST_ACCOUNTS: ListAccounts = { watched: new Set(), aiIgnored: false };

/**
 * Whether Activity could ever show this event. Sends (AI or team) and
 * subscriptions never show; list events only on Accounts with watched lists,
 * or for fans the AI ignores when that is switched on. The rest would only
 * push real events out of the cap (list events alone are most of the traffic,
 * AI-ignored fans — mostly spam — the biggest part of them).
 */
export function keepForActivity(ev: WorkspaceEvent, accounts: ListAccounts): boolean {
  switch (ev.kind) {
    case 'fan_message':
    case 'tip':
    case 'ppv_bought':
    case 'handoff':
    case 'hot_enter':
      return true;
    case 'fan_lists':
      return accounts.watched.has(ev.account_id) || (!!ev.payload?.ignored && accounts.aiIgnored);
    default:
      return false;
  }
}

const atMs = (e: WorkspaceEvent) => Date.parse(e.occurred_at) || 0;

/** Events oldest first, unique by id, capped. New events usually just append. */
export function mergeEvents(prev: WorkspaceEvent[], incoming: WorkspaceEvent[], max = MAX_EVENTS): WorkspaceEvent[] {
  if (!incoming.length) return prev;
  const seen = new Set(prev.map((e) => e.id));
  const fresh: WorkspaceEvent[] = [];
  for (const e of incoming) {
    if (!e || !e.id || seen.has(e.id)) continue;
    seen.add(e.id);
    fresh.push(e);
  }
  if (!fresh.length) return prev;
  const at = new Map<WorkspaceEvent, number>();
  const key = (e: WorkspaceEvent) => {
    let v = at.get(e);
    if (v === undefined) at.set(e, (v = atMs(e)));
    return v;
  };
  fresh.sort((a, b) => key(a) - key(b));
  const all = !prev.length || key(fresh[0]!) >= key(prev[prev.length - 1]!) ? prev.concat(fresh) : [...prev, ...fresh].sort((a, b) => key(a) - key(b));
  return all.length > max ? all.slice(all.length - max) : all;
}

/** Collects frames between ticks. */
export class FrameBatch {
  private replayed: WorkspaceEvent[] = [];
  private live: WorkspaceEvent[] = [];
  private states = new Map<string, ChatStateRow>();
  private badge: number | null = null;

  get empty(): boolean {
    return !this.replayed.length && !this.live.length && !this.states.size && this.badge == null;
  }

  replay(events: WorkspaceEvent[]) {
    for (const e of events) if (e) this.replayed.push(e);
  }

  event(ev: WorkspaceEvent) {
    if (ev) this.live.push(ev);
  }

  chatState(row: ChatStateRow) {
    if (row) this.states.set(chatKey(row.account_id, row.fan_id), row);
  }

  setBadge(count: number) {
    this.badge = count;
  }

  /** Applies everything collected to `prev` and starts a new batch. */
  drain(prev: LiveSnapshot, accounts: ListAccounts): LiveDrain {
    const keep = (e: WorkspaceEvent) => keepForActivity(e, accounts);
    const incoming = [...this.replayed.filter(keep), ...this.live.filter(keep)];
    const events = mergeEvents(prev.events, incoming);
    let chatStates = prev.chatStates;
    if (this.states.size) {
      chatStates = new Map(prev.chatStates);
      for (const [k, row] of this.states) chatStates.set(k, row);
    }
    const out: LiveDrain = {
      snapshot: events === prev.events && chatStates === prev.chatStates ? prev : { events, chatStates },
      live: this.live,
      states: [...this.states.values()],
      badge: this.badge,
    };
    this.replayed = [];
    this.live = [];
    this.states = new Map();
    this.badge = null;
    return out;
  }

  clear() {
    this.replayed = [];
    this.live = [];
    this.states = new Map();
    this.badge = null;
  }
}

/** Which Accounts' list events to keep, from saved Activity settings. */
export function listAccountsOf(activity: unknown): ListAccounts {
  const a = activity as { watchedLists?: Record<string, unknown>; showAiIgnored?: unknown; kinds?: { list?: unknown } } | undefined;
  if (a?.kinds?.list === false) return NO_LIST_ACCOUNTS;
  const watched = new Set<string>();
  if (a?.watchedLists && typeof a.watchedLists === 'object') {
    for (const [acc, names] of Object.entries(a.watchedLists)) if (Array.isArray(names) && names.length) watched.add(acc);
  }
  return { watched, aiIgnored: a?.showAiIgnored === true };
}
